import './styles.scss';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Icon,
} from '@edx/paragon';
import {
  ArrowForward,
  Person,
  Timelapse,
  Program,
} from '@edx/paragon/icons';
import { injectIntl, FormattedMessage, defineMessages } from 'gatsby-plugin-react-intl';

import { intlShape } from '@prospectus/common/types/intlShape';
import {
  getProgramDuration,
  getProgramPacing,
  getTotalEstimatedEffortInHoursPerWeek, programIsMicroMasters, programIsProfessionalCertificate,
} from '@utils/program';
import activeProgramShape from '@prospectus/common/types/activeProgramShape';
import ProspectusLink from '@prospectus/common/ui-strict/ProspectusLink';

const messages = defineMessages({
  'prospectus.program.program.upsell.header': {
    id: 'prospectus.program.program.upsell.header',
    defaultMessage: 'This course is part of',
    description: 'Heading for the program upsell',
  },
  'prospectus.program.program.upsell.title': {
    id: 'prospectus.program.program.upsell.title',
    defaultMessage: '{programTitle} {programType} Program',
    description: 'Title of the Program upsell',
  },
  'prospectus.program.program.upsell.cta-learn-more': {
    id: 'prospectus.program.program.upsell.cta-learn-more',
    defaultMessage: 'Learn more',
    description: 'Title for the program click button',
  },
});

const getExpertInstructionSecondaryLabel = (type, courseCount) => {
  if (programIsMicroMasters(type)) {
    return (
      <FormattedMessage
        id="prospectus.program.program.upsell.instruction.description.micromasters"
        defaultMessage="{courseCount} graduate-level courses"
        description="Description for the statistic describing expert instruction."
        values={{
          courseCount,
        }}
      />
    );
  }
  if (programIsProfessionalCertificate(type)) {
    return (
      <FormattedMessage
        id="prospectus.program.program.upsell.instruction.description.professional_certificate"
        defaultMessage="{courseCount} skill-building courses"
        description="Description for the statistic describing expert instruction."
        values={{
          courseCount,
        }}
      />
    );
  }
  return (
    <FormattedMessage
      id="prospectus.program.program.upsell.instruction.description.xseries"
      defaultMessage="{courseCount} high-quality courses"
      description="Description for the statistic describing expert instruction."
      values={{
        courseCount,
      }}
    />
  );
};

function ProgramUpsellWidget(props) {
  const { intl, program, courseKey } = props;

  const cleanedProgramCourses = useMemo(() => (program.courses.map(({ course }) => course)), [program]);
  const {
    programPacingType,
    totalEstimatedDuration,
    hoursPerWeekFormatted,
  } = useMemo(() => ({
    programPacingType: getProgramPacing(cleanedProgramCourses),
    totalEstimatedDuration: getProgramDuration(cleanedProgramCourses, intl),
    hoursPerWeekFormatted: getTotalEstimatedEffortInHoursPerWeek(cleanedProgramCourses),
  }), [cleanedProgramCourses]);

  return (
    <div className="bg-dark-400 py-5.5 mt-5">
      <Container size="lg" className="program-upsell-container">
        <div className="text-primary-100 program-upsell">
          <div className="d-flex flex-column position-relative program-upsell-heading">
            <h1 className="mb-4.5 text-primary-100">
              <span>
                {intl.formatMessage(
                  messages['prospectus.program.program.upsell.header'],
                )}
              </span>
              <span className="text-accent-b">
                &nbsp;
                {intl.formatMessage(
                  messages['prospectus.program.program.upsell.title'],
                  {
                    programTitle: program.title,
                    programType: program.type,
                  },
                )}
              </span>
            </h1>
            <ProspectusLink
              href={program.marketingPath}
              analytics={{
                eventName: 'edx.bi.user.programbanner.recommendations.clicked',
                linkProps: {
                  is_control: false,
                  program_uuid: program.uuid,
                  page: 'course_about_page',
                  course_key: courseKey,
                },
              }}
              className="btn btn-brand text-decoration-none program-upsell-cta"
            >
              <span>
                {intl.formatMessage(messages['prospectus.program.program.upsell.cta-learn-more'])}
              </span>&nbsp;<Icon src={ArrowForward} className="pl-2 d-inline-block learn-more-arrow-icon" />
            </ProspectusLink>
          </div>
          <div className="d-flex flex-column w-100 program-upsell-details">
            <div className="d-flex align-items-center mb-4.5 program-upsell-item">
              <Icon src={Program} />
              <div className="ml-3">
                <div className="h3 mb-0 text-primary-100">
                  <FormattedMessage
                    id="prospectus.program.program.upsell.expert.instruction"
                    defaultMessage="Expert instruction"
                    description="Title for the statistic describing expert instruction."
                  />
                </div>
                <div className="small text-light-400">
                  { getExpertInstructionSecondaryLabel(program.type, program.courses.length) }
                </div>
              </div>
            </div>
            {programPacingType === 'self_paced'
              && (
              <div className="d-flex align-items-center mb-4.5 program-upsell-item">
                <Icon src={Person} />
                <div className="ml-3">
                  <div className="h3 mb-0 text-primary-100">
                    <FormattedMessage
                      id="prospectus.program.program.upsell.selfPaced.title"
                      defaultMessage="Self-paced"
                      description="Title for the statistic describing self paced learning."
                    />
                  </div>
                  <div className="small text-light-400">
                    <FormattedMessage
                      id="prospectus.program.program.upsell.selfPaced.description"
                      defaultMessage="Progress at your own speed"
                      description="Description for the statistic describing self paced learning."
                    />
                  </div>
                </div>
              </div>
              )}
            {programPacingType === 'instructor_paced'
              && (
              <div className="d-flex align-items-center mb-4.5 program-upsell-item">
                <Icon src={Person} />
                <div className="ml-3">
                  <div className="h3 mb-0 text-primary-100">
                    <FormattedMessage
                      id="prospectus.program.program.upsell.instructorLed.title"
                      defaultMessage="Instructor-led"
                      description="Title for the statistic describing instructor led learning."
                    />
                  </div>
                  <div className="small text-light-400">
                    <FormattedMessage
                      id="prospectus.program.program.upsell.instructorLed.description"
                      defaultMessage="Assignments and exams have specific due dates"
                      description="Description for the statistic describing instructor led learning."
                    />
                  </div>
                </div>
              </div>
              )}
            {totalEstimatedDuration && hoursPerWeekFormatted
            && (
            <div className="d-flex align-items-center program-upsell-item">
              <Icon src={Timelapse} />
              <div className="ml-3">
                <div className="h3 mb-0 text-primary-100">
                  <FormattedMessage
                    id="prospectus.program.program.upsell.duration"
                    defaultMessage="{duration}"
                    description="Describe estimated duration of the program."
                    values={{
                      duration: totalEstimatedDuration,
                    }}
                  />
                </div>
                <div className="small text-light-400">
                  <FormattedMessage
                    id="prospectus.program.program.upsell.averageEffort.description"
                    defaultMessage="{totalHours} hours per week"
                    description="Description for the statistic describing average hours of effort per week."
                    values={{
                      totalHours: hoursPerWeekFormatted,
                    }}
                  />
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
}

ProgramUpsellWidget.propTypes = {
  intl: intlShape.isRequired,
  program: PropTypes.shape(activeProgramShape),
  courseKey: PropTypes.string,
};

ProgramUpsellWidget.defaultProps = {
  program: {},
  courseKey: null,
};

export default injectIntl(ProgramUpsellWidget);
